import type { AppLanguage } from '../i18n/types';
import {
  TIME_HOURS_24,
  TIME_MINUTES_60,
  formatRoundHourTimeLabel,
  joinTimeHmParts,
  normalizeTimeHm,
  normalizeHourOptionList,
  parseTimeHmParts,
  uses24HourTimePicker,
} from '../utils/timeLocale';
import styles from './LocaleTimeInput.module.css';

type LocaleTimeInputProps = {
  id: string;
  value: string;
  onChange: (next: string) => void;
  language: AppLanguage;
  disabled?: boolean;
  readOnly?: boolean;
  /** Booking flows: round hours only (`HH:00`); minutes shown as `00`, not selectable. */
  wholeHoursOnly?: boolean;
  /** Restrict selectable hours (HH or HH:00). */
  hours?: readonly string[];
  className?: string;
  selectClassName?: string;
  ariaLabel?: string;
};

/** Time picker: 24h hour/minute selects in Hebrew; native `<input type="time">` in English. */
export function LocaleTimeInput({
  id,
  value,
  onChange,
  language,
  disabled = false,
  readOnly = false,
  wholeHoursOnly = false,
  hours,
  className,
  selectClassName,
  ariaLabel,
}: LocaleTimeInputProps) {
  const frozen = disabled || readOnly;
  const hm = normalizeTimeHm(value);

  if (wholeHoursOnly) {
    const hourOptions = normalizeHourOptionList(hours && hours.length > 0 ? hours : TIME_HOURS_24);
    const { hour } = parseTimeHmParts(hm);
    const selectedHour = hourOptions.includes(hour) ? hour : (hourOptions[0] ?? '09');
    const selectClass = selectClassName ?? className ?? styles.select;
    const hourWord = language === 'he' ? 'שעה' : 'hour';
    const minuteWord = language === 'he' ? 'דקות' : 'minutes';
    return (
      <select
        id={id}
        className={selectClass}
        value={selectedHour}
        disabled={frozen || hourOptions.length === 0}
        aria-label={ariaLabel ?? `${hourWord}, ${minuteWord} 00`}
        onChange={(event) => onChange(joinTimeHmParts(event.target.value, '00'))}
      >
        {hourOptions.map((h) => (
          <option key={h} value={h}>
            {formatRoundHourTimeLabel(h)}
          </option>
        ))}
      </select>
    );
  }

  if (!uses24HourTimePicker(language)) {
    return (
      <input
        id={id}
        className={className}
        type="time"
        step={3600}
        value={hm}
        readOnly={readOnly}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange(normalizeTimeHm(event.target.value))}
      />
    );
  }

  const { hour, minute } = parseTimeHmParts(hm);
  const selectClass = selectClassName ?? className ?? styles.select;
  const hourWord = language === 'he' ? 'שעה' : 'hour';
  const minuteWord = language === 'he' ? 'דקות' : 'minutes';

  return (
    <div className={styles.row} role="group" aria-label={ariaLabel}>
      <select
        id={`${id}-minute`}
        className={selectClass}
        value={minute}
        disabled={frozen}
        aria-label={ariaLabel ? `${ariaLabel} — ${minuteWord}` : undefined}
        onChange={(event) => onChange(joinTimeHmParts(hour, event.target.value))}
      >
        {TIME_MINUTES_60.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <span className={styles.sep} aria-hidden="true">
        :
      </span>
      <select
        id={`${id}-hour`}
        className={selectClass}
        value={hour}
        disabled={frozen}
        aria-label={ariaLabel ? `${ariaLabel} — ${hourWord}` : undefined}
        onChange={(event) => onChange(joinTimeHmParts(event.target.value, minute))}
      >
        {TIME_HOURS_24.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
    </div>
  );
}
